/**
 * Shared theme storage for GolfAuth Core and Fitting.
 *
 * Users pick System / Light / Dark in Settings. With no explicit choice the
 * apps follow the device's `prefers-color-scheme`, so a fresh install matches
 * the phone or desktop it lands on.
 *
 * The choice lives in the `golfauth-theme-pref` cookie (shared across
 * `.golfauth.com` so Core at /core/ and Fitting agree). The older
 * `golfauth-theme` cookie is deliberately ignored: the dark-only build (D1,
 * May 2026) force-wrote `dark` into it on every load, so it cannot tell a
 * real choice from the forced default.
 *
 * The same value is mirrored to localStorage under the same key. The
 * cookie stays primary (it is the only store shared across subdomains on
 * the web), but iOS WKWebView does not keep cookies for the Capacitor app's
 * `capacitor://localhost` page, so a Dark/Light pick reverted to System on
 * every relaunch. Reads fall back to localStorage when there is no cookie.
 *
 * Both apps' inline preheader scripts in index.html mirror `resolveTheme`
 * before paint — keep the cookie name, the localStorage fallback and the
 * resolution rules in sync.
 */

export type Theme = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_COOKIE_NAME = 'golfauth-theme-pref';
const COOKIE_MAX_AGE = 365 * 24 * 60 * 60; // 1 year

function isTheme(value: unknown): value is Theme {
  return value === 'light' || value === 'dark' || value === 'system';
}

function getCookieDomain(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') return 'localhost';
  if (host.endsWith('.golfauth.com') || host === 'golfauth.com') return '.golfauth.com';
  return undefined;
}

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.split('; ').find((part) => part.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : undefined;
}

function setCookie(name: string, value: string): void {
  const domain = getCookieDomain();
  let cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;
  if (domain) cookie += `; domain=${domain}`;
  // Chrome drops a `Secure` cookie set over plain http, so only add it on https.
  if (window.location.protocol === 'https:') cookie += '; Secure';
  document.cookie = cookie;
}

// localStorage can be missing (SSR, tests) or throw (blocked storage,
// private mode), so every access is guarded.
function readLocal(name: string): string | undefined {
  try {
    return (typeof window !== 'undefined' && window.localStorage?.getItem(name)) || undefined;
  } catch {
    return undefined;
  }
}

function writeLocal(name: string, value: string): void {
  try {
    window.localStorage?.setItem(name, value);
  } catch {
    /* storage unavailable — the cookie still carries the choice on the web */
  }
}

/** The user's saved choice, or `fallback` (default 'system') if none. */
export function getTheme(fallback: Theme = 'system'): Theme {
  const cookie = readCookie(THEME_COOKIE_NAME);
  if (isTheme(cookie)) return cookie;
  const local = readLocal(THEME_COOKIE_NAME);
  return isTheme(local) ? local : fallback;
}

export function setTheme(theme: Theme): void {
  if (typeof window === 'undefined') return;
  setCookie(THEME_COOKIE_NAME, theme);
  writeLocal(THEME_COOKIE_NAME, theme);
}

export function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'dark';
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

export function getResolvedTheme(theme: Theme): ResolvedTheme {
  return theme === 'system' ? getSystemTheme() : theme;
}
